"use client";
import { Fragment, useState } from "react";
import BFLLink from "@repo/shared-ui/BFLLink";
import Products from "./Products";
import {
  handleFooterAccordionClickDL,
  handleFooterLinkClickDL,
  handleFooterSocialMediaClickDL,
} from "./FooterAccordionFunction";
import Conditional from "@repo/shared-ui/Conditional";

interface AccordionItem {
  title: string;
  content: Record<string, string>[];
}

interface FooterAccordionProps {
  loanLinks: Record<string, string>[];
  reachUsData: Record<string, string>[];
  calculatorData: Record<string, string>[];
  legalData: Record<string, string>[];
  productsData: Record<string, string>[];
  socialMediaIcons: Record<string, string>[];
}

// Helper function to handle link click
const handleLinkClickEvent = (
  e: React.MouseEvent<HTMLElement>,
  linkTitle: string,
  sectionTitle: string,
  linkUrl: string
) => {
  e.preventDefault();
  handleFooterLinkClickDL(linkTitle, sectionTitle, linkUrl);
  window.location.href = linkUrl;
};

// Helper function to render application form links
const renderApplicationFormLinks = (content: Record<string, string>[], sectionTitle: string) => {
  const contentArr = Array.isArray(content) ? content : [];
  const first = contentArr[0] as Record<string, string | Record<string, string>[]>;

  if (first && Array.isArray(first.children)) {
    return first.children.map((link: Record<string, string>, idx: number) => {
      const linkUrl = link.linkUrl || "#";
      const linkTitle = link?.linkTitle || "";

      return (
        <BFLLink
          key={link?.nodeName || linkTitle || idx}
          href={linkUrl}
          className="my-1 w-[50%] pr-2 leading-[19.2px] md:!w-1/5"
          onClick={(e) => handleLinkClickEvent(e, linkTitle, sectionTitle, linkUrl)}
        >
          {linkTitle}
        </BFLLink>
      );
    });
  }
  return null;
};

// Helper function to render regular links
const renderRegularLinks = (
  content: Record<string, string>[],
  sectionTitle: string,
  isReachUs: boolean
) => {
  return (content as Record<string, string>[])?.map((link, idx) => {
    const linkUrl = link.linkUrl || "#";
    const linkTitle = link?.linkTitle || "";

    return (
      <BFLLink
        key={link?.nodeName || linkTitle || idx}
        href={linkUrl}
        className={`my-1 w-[50%] leading-[19.2px] ${
          isReachUs ? "pr-0 md:w-[50%]" : "pr-2 md:!w-1/5"
        }`}
        onClick={(e) => handleLinkClickEvent(e, linkTitle, sectionTitle, linkUrl)}
      >
        {linkTitle}
      </BFLLink>
    );
  });
};

// Helper function to render links based on section type
const renderLinks = (item: AccordionItem) => {
  if (item.title === "Application forms") {
    return renderApplicationFormLinks(item.content, item.title);
  }
  return renderRegularLinks(item.content, item.title, item.title === "Reach Us");
};

const RenderSocialMediaIcons = (type: string, url: string, index: number, sectionTitle: string) => {
  const commonClass = "w-8 h-8 rounded-full p-1 flex-center-center text-sm";
  const ytClass = "bg-yt-red";
  const fbClass = "bg-fb-blue";
  const xClass = "bg-black";
  const lInClass = "bg-lin-blue";

  const handleSocialMediaClick = (
    e: React.MouseEvent<HTMLElement>,
    platform: string,
    mediaUrl: string,
    tabPosition: number
  ) => {
    e.preventDefault();

    // Track analytics before navigation
    handleFooterSocialMediaClickDL(platform, mediaUrl, sectionTitle, tabPosition);

    // Navigate after analytics completes
    window.location.href = mediaUrl;
  };

  return (
    <Fragment key={`${type}-${index}`}>
      <Conditional if={type === "Facebook"}>
        <BFLLink
          href={url}
          className={`${commonClass} ${fbClass}`}
          onClick={(e) => handleSocialMediaClick(e, type, url, index)}
        >
          <i className="icomoon before:content-['\e915']" />
        </BFLLink>
      </Conditional>
      <Conditional if={type === "LinkedIn"}>
        <BFLLink
          href={url}
          className={`${commonClass} ${lInClass}`}
          onClick={(e) => handleSocialMediaClick(e, type, url, index)}
        >
          <i className="icomoon before:content-['\e917']" />
        </BFLLink>
      </Conditional>
      <Conditional if={type === "Twitter"}>
        <BFLLink
          href={url}
          className={`${commonClass} ${xClass}`}
          onClick={(e) => handleSocialMediaClick(e, type, url, index)}
        >
          <i className="icomoon before:content-['\e95a']" />
        </BFLLink>
      </Conditional>
      <Conditional if={type === "YouTube"}>
        <BFLLink
          href={url}
          className={`${commonClass} ${ytClass}`}
          onClick={(e) => handleSocialMediaClick(e, type, url, index)}
        >
          <i className="icomoon before:content-['\e920']" />
        </BFLLink>
      </Conditional>
    </Fragment>
  );
};

const FooterAccordion: React.FC<FooterAccordionProps> = ({
  loanLinks,
  reachUsData,
  calculatorData,
  legalData,
  productsData,
  socialMediaIcons,
}) => {
  const accordionData: AccordionItem[] = [
    {
      title: "Application forms",
      content: loanLinks,
    },
    {
      title: "Products portfolio",
      content: productsData,
    },
    {
      title: "Calculators",
      content: Object.values(calculatorData),
    },
    {
      title: "Legal",
      content: Object.values(legalData),
    },
    {
      title: "Reach Us",
      content: Object.values(reachUsData),
    },
  ];
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const toggleAccordion = (index: number) => {
    // Track analytics event for accordion click
    const title = accordionData[index]?.title ?? "";
    // Pass loginStatus (empty string), cta_text (accordion title), and tab_position (index)
    handleFooterAccordionClickDL(title, index);

    setOpenIndex(openIndex === index ? null : index);
  };
  return (
    <div className="mx-auto w-full cursor-pointer">
      {accordionData.map((item, index) => (
        <div key={"footacc-" + index} className="border-stealblue-100 border-b px-4 py-3">
          <button
            onClick={() => toggleAccordion(index)}
            className="bg-bfl-blue flex w-full cursor-pointer items-center justify-between text-left text-base font-medium capitalize transition"
            data-section-title={item.title}
          >
            {item.title}
            <i
              className={`icomoon transform transition-transform duration-300 before:content-['\\e909'] ${
                openIndex === index ? "rotate-180" : ""
              }`}
            ></i>
          </button>
          <div
            className={`bg-bfl-blue transition-all duration-300 ease-in-out ${
              openIndex === index ? "" : "hidden"
            } ${item.title === "Reach Us" ? "flex flex-col md:flex-row" : "block"}`}
          >
            {item.title === "Products portfolio" ? (
              <Products data={productsData} />
            ) : (
              <>
                <div
                  className={`flex flex-wrap text-sm ${
                    item.title === "Reach Us" ? "w-full lg:!w-[40%]" : "w-full"
                  }`}
                >
                  {renderLinks(item)}
                </div>
                {item.title === "Reach Us" && (
                  <div className="flex gap-4 py-2 md:px-6">
                    {Object.values(socialMediaIcons).map(({ titleText, socialMediaUrl }, idx) =>
                      RenderSocialMediaIcons(titleText || "", socialMediaUrl || "", idx, item.title)
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};
export default FooterAccordion;
